import { Dialog } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ASK_AI_SERVICE, AskAiResult, ToastService } from '@services';
import { SpinnerComponent } from '@ui-lib';
import {
  AskAiResultDialogComponent,
  AskAiResultDialogData,
} from '../../ask-ai/ask-ai-result-dialog/ask-ai-result-dialog.component';
import { CurrentPatientService } from '../current-patient.service';

type AskAiMode = 'idle' | 'pending' | 'completed';

const MAX_TEXTAREA_HEIGHT_PX = 160;

@Component({
  selector: 'provider-portal-ask-ai-input',
  imports: [MatIconModule, SpinnerComponent],
  templateUrl: './ask-ai-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      position: relative;
      display: block;
    }
    @property --ai-border-angle {
      syntax: '<angle>';
      initial-value: 90deg;
      inherits: false;
    }
    .ai-pending-border {
      border: 2px solid transparent;
      background:
        linear-gradient(to bottom, #fafcff, #f5f9ff) padding-box,
        conic-gradient(
            from var(--ai-border-angle) at 50% 50%,
            #9fb6e8 0deg,
            #b71697 85.69deg,
            #116f91 183.46deg,
            #5175c5 259.62deg,
            #9fb6e8 360deg
          )
          border-box;
      animation: ai-border-spin 3s linear infinite;
    }

    @keyframes ai-border-spin {
      to {
        --ai-border-angle: 450deg;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .ai-pending-border {
        animation: none;
      }
    }
  `,
})
export class AskAiInputComponent {
  private readonly dialog = inject(Dialog);
  private readonly toast = inject(ToastService);
  private readonly askAiService = inject(ASK_AI_SERVICE);
  private readonly currentPatientService = inject(CurrentPatientService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly textareaRef =
    viewChild<ElementRef<HTMLTextAreaElement>>('textarea');

  protected readonly mode = signal<AskAiMode>('idle');
  protected readonly query = signal('');
  protected readonly showPrompt = signal(false);
  protected readonly isMultiline = signal(false);

  /** The submitted question + patient, captured at submit time so the result
   * pop-up stays correct even if the provider navigates while it generates. */
  protected readonly lastQuestion = signal('');
  protected readonly submittedPatientName = signal<string | null>(null);

  private readonly result = signal<AskAiResult | null>(null);
  private readonly dismissedPatientId = signal<string | null>(null);

  private readonly currentPatient = this.currentPatientService.currentPatient;

  /** Current patient unless its chip was dismissed for this query. Auto-resets
   * when the provider opens a different patient's chart. */
  protected readonly activePatient = computed(() => {
    const patient = this.currentPatient();
    if (!patient) return null;
    return this.dismissedPatientId() === patient.id ? null : patient;
  });

  protected readonly patientName = computed(() => {
    const patient = this.activePatient();
    return patient ? `${patient.givenName} ${patient.familyName}`.trim() : null;
  });

  protected readonly placeholder = computed(() =>
    this.patientName() ? 'Ask AI about this patient…' : 'Ask AI…'
  );

  protected readonly canSubmit = computed(() => this.query().trim().length > 0);

  constructor() {
    effect(
      () => {
        const textarea = this.textareaRef()?.nativeElement;
        if (textarea && this.query()) this.autoGrow(textarea);
      },
      { allowSignalWrites: true }
    );
  }

  protected onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.query.set(textarea.value);
    this.autoGrow(textarea);
  }

  protected onEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    this.submit();
  }

  protected removePatientChip(): void {
    const patient = this.currentPatient();
    if (patient) this.dismissedPatientId.set(patient.id);
  }

  protected clearQuery(): void {
    this.query.set('');
    this.isMultiline.set(false);
    this.resetTextareaHeight();
  }

  protected submit(): void {
    const prompt = this.query().trim();
    if (!prompt || this.mode() === 'pending') return;

    this.lastQuestion.set(prompt);
    this.submittedPatientName.set(this.patientName());
    this.showPrompt.set(false);
    this.mode.set('pending');

    this.askAiService
      .ask(prompt, this.activePatient()?.id ?? null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.result.set(result);
          this.mode.set('completed');
          this.openResult(result);
        },
        error: () => {
          this.mode.set('idle');
          this.toast.showWarning(
            'Sorry, something went wrong. Please try again.'
          );
        },
      });
  }

  protected toggleShowPrompt(): void {
    this.showPrompt.update(value => !value);
  }

  protected viewResult(): void {
    const result = this.result();
    if (result) this.openResult(result);
  }

  protected startOver(): void {
    this.mode.set('idle');
    this.query.set('');
    this.result.set(null);
    this.lastQuestion.set('');
    this.submittedPatientName.set(null);
    this.showPrompt.set(false);
    this.resetTextareaHeight();
  }

  private openResult(result: AskAiResult): void {
    const patientName = this.submittedPatientName();
    const data: AskAiResultDialogData = {
      headerTitle: patientName
        ? `AI insights about ${patientName}`
        : 'AI insights',
      question: this.lastQuestion(),
      result,
    };
    this.dialog.open(AskAiResultDialogComponent, { data });
  }

  private autoGrow(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT_PX);
    textarea.style.height = `${newHeight}px`;
    this.isMultiline.set(textarea.scrollHeight > 40);
  }

  private resetTextareaHeight(): void {
    const textarea = this.textareaRef()?.nativeElement;
    if (textarea) textarea.style.height = 'auto';
  }
}
